import { z } from 'zod';

/**
 * The only permission names the service knows (BB-15). Modules never invent their own:
 * a new permission is added here in its own change, together with the role-matrix test in `@bb/auth`.
 */
export const PERMISSIONS = [
  'org.manage',
  'env.view',
  'env.manage',
  'apps.manage',
  'keys.manage',
  'integrations.manage',
  'employees.manage',
  'catalog.edit',
  'segments.edit',
  'content.edit',
  'content.custom_code',
  'campaigns.edit',
  'canvas.edit',
  'messages.test_send',
  'messages.test_send.any_profile',
  'messages.approve',
  'messages.publish',
  'messages.exemptions.manage',
  'reports.view',
  'profiles.view',
  'profiles.edit',
  'profiles.import',
  'profiles.delete',
  'profiles.merge',
  'pii.view',
  'data.export',
  'secrets.view',
  'audit.view',
] as const;

export type Permission = (typeof PERMISSIONS)[number];

/** Never part of a role: granted to a person explicitly, per scope (03 §3, ADM-006). */
export const SPECIAL_PERMISSIONS = [
  'pii.view',
  'data.export',
  'profiles.delete',
  'profiles.merge',
  'secrets.view',
  'messages.exemptions.manage',
  'content.custom_code',
] as const satisfies readonly Permission[];

/** Fixed permission sets (03 §3); the role → permissions map lives in `@bb/auth`. "Analyst/Viewer" is one role: `analyst`. */
export const ROLES = ['owner', 'admin', 'developer', 'marketer', 'reviewer', 'publisher', 'analyst'] as const;
export type Role = (typeof ROLES)[number];

export const GRANT_SCOPE_TYPES = ['organization', 'workspace', 'environment'] as const;
export type GrantScopeType = (typeof GRANT_SCOPE_TYPES)[number];

const scope = { scopeType: z.enum(GRANT_SCOPE_TYPES), scopeId: z.uuid() };

export const CreateGrantBody = z.discriminatedUnion('kind', [
  z.strictObject({ kind: z.literal('role'), role: z.enum(ROLES), ...scope }),
  z.strictObject({ kind: z.literal('permission'), permission: z.enum(PERMISSIONS), ...scope }),
]);
export type CreateGrantBody = z.infer<typeof CreateGrantBody>;
